import React from 'react'
import styles from './mhlShared.module.scss'

export interface TabOption<T extends string> {
    value: T
    label: string
    count?: number | null
}

interface Props<T extends string> {
    value: T
    options: TabOption<T>[]
    onChange: (value: T) => void
    ariaLabel?: string
}

/** Underline-style tab strip used to split webform vs dashboard leads. */
export const SourceTabs = <T extends string>({ value, options, onChange, ariaLabel }: Props<T>) => (
    <div className={styles.tabs} role="tablist" aria-label={ariaLabel}>
        {options.map((opt) => (
            <button
                key={opt.value}
                type="button"
                role="tab"
                aria-selected={value === opt.value}
                className={`${styles.tab} ${value === opt.value ? styles.tabActive : ''}`}
                onClick={() => onChange(opt.value)}
            >
                {opt.label}
                {opt.count !== undefined && opt.count !== null && <span className={styles.tabCount}>{opt.count}</span>}
            </button>
        ))}
    </div>
)

export const Pill = ({
    tone = 'muted',
    children,
    dot = false,
}: {
    tone?: 'good' | 'bad' | 'warn' | 'info' | 'muted' | 'brand'
    children: React.ReactNode
    dot?: boolean
}) => (
    <span className={`${styles.pill} ${styles[tone]}`}>
        {dot && <span className={styles.pillDot} />}
        {children}
    </span>
)

export const StatTile = ({
    label,
    value,
    note,
    tone,
    accent,
    onClick,
}: {
    label: string
    value: React.ReactNode
    note?: React.ReactNode
    tone?: 'good' | 'bad' | 'warn'
    accent?: boolean
    onClick?: () => void
}) => (
    <div
        className={`${styles.statTile} ${accent ? styles.accent : ''} ${onClick ? styles.clickable : ''}`}
        onClick={onClick}
        onKeyDown={onClick ? (e) => (e.key === 'Enter' || e.key === ' ') && onClick() : undefined}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
    >
        <span className={styles.statLabel}>
            {tone && <span className={`${styles.statDot} ${styles[tone]}`} />}
            {label}
        </span>
        <span className={`${styles.statValue} ${value === 0 ? styles.zero : ''}`}>{value}</span>
        {note && <span className={styles.statNote}>{note}</span>}
    </div>
)
